import { useState, useCallback, useRef } from "react";
import { api, type GrammarSuggestion } from "@/lib/api";

interface GrammarCheckState {
  isOpen: boolean;
  isLoading: boolean;
  suggestions: GrammarSuggestion[];
  error: string | null;
}

interface UseGrammarCheckReturn {
  state: GrammarCheckState;
  open: () => void;
  close: () => void;
  check: (text: string) => Promise<void>;
  apply: (index: number) => GrammarSuggestion | null;
  dismiss: (index: number) => void;
}

/**
 * Hook for managing grammar check state and API calls.
 * Handles loading, displaying suggestions, and applying/dismissing individual items.
 */
export function useGrammarCheck(): UseGrammarCheckReturn {
  const [state, setState] = useState<GrammarCheckState>({
    isOpen: false,
    isLoading: false,
    suggestions: [],
    error: null,
  });
  // Apply reads the suggestion now, not from a state updater React may run later.
  const suggestionsRef = useRef<GrammarSuggestion[]>([]);
  suggestionsRef.current = state.suggestions;

  const open = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: true }));
  }, []);

  const close = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: false,
      suggestions: [],
      error: null,
    }));
  }, []);

  const check = useCallback(async (text: string): Promise<void> => {
    setState((prev) => ({
      ...prev,
      isOpen: true,
      isLoading: true,
      error: null,
      suggestions: [],
    }));

    try {
      // Signed in like every other call (a bare fetch carried no token: always 401).
      const data = await api.ai.grammarCheck(text);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        suggestions: data.suggestions,
      }));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      setState((prev) => ({ ...prev, isLoading: false, error: message }));
    }
  }, []);

  const apply = useCallback((index: number): GrammarSuggestion | null => {
    const applied = suggestionsRef.current[index] ?? null;
    if (!applied) return null;
    setState((prev) => ({ ...prev, suggestions: prev.suggestions.filter((_, i) => i !== index) }));
    return applied;
  }, []);

  const dismiss = useCallback((index: number) => {
    setState((prev) => ({
      ...prev,
      suggestions: prev.suggestions.filter((_, i) => i !== index),
    }));
  }, []);

  return {
    state,
    open,
    close,
    check,
    apply,
    dismiss,
  };
}
